/**
 * <ActivityStatsCard> — « Mes statistiques », sur le compte de l'élève (organism).
 *
 * Quatre chiffres d'activité (le temps, les activités réalisées, les jours d'activité, le score
 * qualité) sur une période. L'ancien écran alternait entre deux périodes au toucher d'un seul
 * bouton ; ici les deux se lisent d'emblée, en pilules de sélection.
 *
 * La période affichée est un état d'interface : elle vit ici. Les chiffres arrivent déjà formulés,
 * période par période.
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Stack } from "../../atoms/containers/Stack"
import { ReferralStatTile } from "../../molecules/ReferralStatTile"
import { SettingsSection } from "../../molecules/SettingsSection"

/** L'intitulé de la carte : il ne dépend d'aucune donnée, son squelette l'écrit aussi. */
export const ACTIVITY_STATS_TITLE = "Mes statistiques"

/** À remonter dans `ports/primary/ui/types` au câblage. */
export type UIActivityStatsTile = {
    key: string
    /** Déjà un libellé : « 45 », « 1h20 », « - ». */
    value: string
    /** « minutes d'activité », « jours d'activité sur 7 ». */
    label: string
}

export type UIActivityStatsPeriod = {
    key: string
    /** « Aujourd'hui », « Depuis lundi dernier ». */
    label: string
    tiles: UIActivityStatsTile[]
}

export type ActivityStatsCardProps = {
    periods: UIActivityStatsPeriod[]
    /** La période montrée à l'ouverture. Défaut : la première. */
    initialPeriodKey?: string
    testID?: string
}

export function ActivityStatsCard(props: ActivityStatsCardProps) {
    const { periods, initialPeriodKey, testID } = props
    const [periodKey, setPeriodKey] = useState(initialPeriodKey ?? periods[0]?.key)

    const period = periods.find((candidate) => candidate.key === periodKey) ?? periods[0]
    if (period == null) return null

    return (
        <SettingsSection title={ACTIVITY_STATS_TITLE} testID={testID}>
            <Stack gap="4">
                {periods.length > 1 && (
                    <View style={activityStatsStyles.periods}>
                        {periods.map((candidate) => (
                            <ChoiceChip
                                key={candidate.key}
                                variant="pill"
                                label={candidate.label}
                                selected={candidate.key === period.key}
                                onPress={() => setPeriodKey(candidate.key)}
                            />
                        ))}
                    </View>
                )}
                <View style={activityStatsStyles.grid}>
                    {period.tiles.map((tile) => (
                        <View key={tile.key} style={activityStatsStyles.cell}>
                            <ReferralStatTile value={tile.value} label={tile.label} />
                        </View>
                    ))}
                </View>
            </Stack>
        </SettingsSection>
    )
}

/** La rangée des périodes et la grille des tuiles : son squelette les pose aux mêmes places. */
export const activityStatsStyles = StyleSheet.create((theme) => ({
    periods: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["3"],
    },
    /** Deux tuiles par rangée, comme les compteurs du parrainage : quatre chiffres se lisent en carré. */
    cell: { flexGrow: 1, flexBasis: "45%" },
}))
